import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Recipes.css";
import CategoryFilter from "../components/CategoryFilter";
import recipeCardStyles from '../components/RecipeCard.module.css';
import buttonStyles from '../components/Buttons.module.css';
import pawImg from '../assets/paw.png';
import { isMobileView, isTabletView } from '../utils/deviceUtils';
import { BACKEND_URL } from '../config';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { isRecipeLocked } from '../utils/recipeAccess';
import { isPremium, isAdmin } from '../utils/userAccess';

interface Recipe {
  id: number;
  slug: string;
  title: string;
  description?: string;
  instructions?: string;
  user_id?: number;
  created_at?: string;
  updated_at?: string;
  photo_url: string;
  base_yield_grams?: number;
  ingredients?: any[];
  locked?: boolean;
  unlocked?: boolean;
  is_locked?: boolean;
  access?: string;
}

const Recipes: React.FC = () => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>("Featured");
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const fetchRecipes = async (tag: string) => {
    try {
      const query = tag === "Featured" ? "" : `?tag=${tag.toLowerCase()}`;
      const response = await api.get(`/recipes${query}`);
      setRecipes(response.data);
    } catch (error) {
      console.error("Error fetching recipes:", error);
    }
  };

  useEffect(() => {
    fetchRecipes(selectedTag);
  }, [selectedTag]);

  // Back to Top button state/logic
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="page-container cream-bg">
      {/* Floating Paw Print Background */}
      <div className="hero-paw-bg">
        <img src={pawImg} className="hero-paw-float paw1" alt="" />
        <img src={pawImg} className="hero-paw-float paw2" alt="" />
        <img src={pawImg} className="hero-paw-float paw3" alt="" />
        <img src={pawImg} className="hero-paw-float paw4" alt="" />
        <img src={pawImg} className="hero-paw-float paw5" alt="" />
        <img src={pawImg} className="hero-paw-float paw6" alt="" />
        <img src={pawImg} className="hero-paw-float paw7" alt="" />
      </div>
      <div className="page-intro">
        <h1 className="page-title">
          {selectedTag === "Featured"
          ? "Featured Recipes"
          : `${selectedTag.charAt(0).toUpperCase()}${selectedTag.slice(1)} Recipes`}
        </h1>
        <div className="page-intro-underline" />
      </div>
      <div className="recipe-layout recipes-container">
        <aside className="sidebar">
          <div className="filter-box">
            <h2 className="filter-title">Filter by Category</h2>
            <CategoryFilter selected={selectedTag} onSelect={setSelectedTag} />
          </div>
          <div style={{ marginTop: 18, textAlign: 'left' }}>
            <span
              role="link"
              tabIndex={0}
              className={buttonStyles.textLinkBtn}
              onClick={() => navigate('/recipe-builder')}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/recipe-builder'); }}
            >
              Search by Specific Food
            </span>
          </div>
        </aside>
        <main className="recipesContent">
          <div className="recipes-grid">
            {recipes.map((recipe) => (
              <div
                key={recipe.id}
                className={recipeCardStyles.recipeCard}
                onClick={() => navigate(`/recipes/${recipe.slug}`)}
              >
                <div>
                  <img alt={recipe.title} src={`${BACKEND_URL}${recipe.photo_url}`} className={recipeCardStyles.recipeImage} />
                </div>
                <div className={recipeCardStyles.recipeTextContainer}>
                  <h4 className={recipeCardStyles.recipeTitle}>{recipe.title}</h4>
                  {recipe.description && (
                    <div className={recipeCardStyles.recipeDescription}>{recipe.description}</div>
                  )}
                </div>
                <div>
                  {token && isRecipeLocked(recipe) && !isPremium(user) && !isAdmin(user) && (
                    <div style={{ marginBottom: 8, fontWeight: 700, color: '#b24646' }}>
                      Locked
                    </div>
                  )}
                  <button
                    className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
                    onClick={e => {
                      e.stopPropagation();
                      navigate(`/recipes/${recipe.slug}`);
                    }}
                  >
                    View Recipe
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
      {/* Sticky Back to Top Button */}
      {showBackToTop && !(isMobileView() || isTabletView()) && (
        <button
          className={buttonStyles.backToTopBtn}
          onClick={handleBackToTop}
          aria-label="Back to top"
        >
          ↑ Top
        </button>
      )}
    </div>
  );
};

export default Recipes;
